/**
 * Zone 1 geometry — is a place of death inside the zone, and if not, how far
 * beyond the border is it?
 *
 * The zone test has to be geometric, not textual. Production municipality
 * values include `Toronto, ON`, `Oshawa ON`, `Scarborough`, `NDG`,
 * `Georgetown / Halton` and `laval`, and `Kawartha Lakes` appears as a
 * *province* — any string match dies on that. So: coordinates against the
 * polygon.
 *
 * Runs in the browser rather than in SQL because the database has no PostGIS.
 * At 12 KB and twelve municipalities that is not a hardship.
 *
 * Milton carries `partial: true` — only its eastern part is in the zone, which
 * is why a Milton address still has to be tested against the geometry rather
 * than matched by name.
 *
 * Two zones now: the GTA one and, since 2026-08-27, Quebec — the island of
 * Montreal (twenty municipalities, because "the island" is Montréal plus
 * fifteen demerged towns), Laval, Longueuil, Brossard and Châteauguay.
 */
import zone1On from './zone1.json';
import zone1Qc from './zone1-qc.json';

/** [lng, lat] — GeoJSON order, not the lat/lng humans say. */
type Position = [number, number];
type Ring = Position[];

export type LatLng = { lat: number; lng: number };

export type ZoneRegion = 'ontario' | 'quebec';

/**
 * Testing an address against the wrong province's polygon would confidently
 * report "outside the zone" for somewhere hundreds of kilometres away, so the
 * region is a required argument rather than a default.
 */
const ZONES: Record<ZoneRegion, any> = {
  ontario: zone1On,
  quebec: zone1Qc,
};

export const zoneFor = (region: ZoneRegion) => ZONES[region];

export type ZoneResult =
  | { inZone: true; municipality: string }
  | {
      inZone: false;
      /** Straight-line kilometres from the nearest point on the zone border. */
      straightLineKm: number;
      /** The border point measured from — quotable, so a family can reproduce it. */
      borderPoint: LatLng;
      /** Which municipality's border that point sits on. */
      borderMunicipality: string;
    };

/**
 * Per-kilometre rates and the QuickBooks item that bills them, by region.
 *
 * Ontario and Quebec charge differently — $3.00 against $2.05 — so this is
 * keyed on region rather than being one number. Getting the wrong one is a 46%
 * error, which is why `transportCharge` takes the region rather than defaulting.
 *
 * Two items, not one, because a single item cannot carry two prices and two tax
 * codes. Quebec kept the original id so the 29 lines billed against it since
 * May 2025 still point at a Quebec-labelled, Quebec-taxed item; Ontario is new.
 *
 * `quickbooks` mirrors each item's unit price so the page can say so when they
 * drift. They agree today (verified 2026-08-27) — the field earns its keep the
 * next time a rate changes and the item is not repriced with it.
 *
 * The annotation is deliberate: as bare literals TypeScript types these as
 * `3` and `2.05`, then rejects comparing them as "no overlap" — killing the
 * check that catches exactly that drift.
 */
export const RATES: Record<
  ZoneRegion,
  {
    perKm: number;
    quickbooks: number;
    quickbooksItemId: string;
    /**
     * QuickBooks sales-tax code for the line. Required, not cosmetic: the
     * contract save refuses to sync an estimate when any line is missing one
     * ("One or more Contract lines missing tax"), so a line written without it
     * silently blocks the whole contract from reaching QuickBooks.
     */
    quickbooksTaxCode: string;
  }
> = {
  /** ON | Outside Zone Transportation, HST ON. */
  ontario: { perKm: 3.0, quickbooks: 3.0, quickbooksItemId: '34', quickbooksTaxCode: '10' },
  /** QC | Outside Zone Transportation — the original item, repriced. GST/QST. */
  quebec: { perKm: 2.05, quickbooks: 2.05, quickbooksItemId: '1010000013', quickbooksTaxCode: '7' },
};

/**
 * Whether the billed distance is the round trip or the single leg.
 *
 * A constant rather than a literal in a formula because the answer is still
 * open and it is a 2x difference — Peterborough is $251 round trip against
 * $125 one way. When it is settled, this is the only line that changes, and
 * every quote moves together. Round trip is the 2026-08-17 assumption.
 */
export const BILL_ROUND_TRIP = true;

/**
 * Straight-line to driving-distance factor, median over the 29 reference towns
 * in docs/pricing/zone-map/distances.json.
 *
 * This is a stand-in until a routing API is wired up: mean error is 10.2% with
 * a tail of -29% (St. Catharines), which is why every figure derived from it is
 * labelled approximate in the UI and must not be read to a family as final.
 */
export const CIRCUITY_FACTOR = 1.195;

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;

export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** Ray casting. Returns true for a point strictly inside the ring. */
function pointInRing(point: LatLng, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Each feature is a MultiPolygon; ring 0 of each polygon is the outer edge, the rest are holes. */
function polygonsOf(feature: any): Ring[][] {
  const geom = feature.geometry;
  return geom.type === 'MultiPolygon' ? geom.coordinates : [geom.coordinates];
}

/**
 * The closest point on segment a-b to p, in degree space.
 *
 * Longitude is scaled by cos(latitude) so a degree of longitude is weighted
 * like a degree of latitude — without it the "nearest" point drifts noticeably
 * east or west at Toronto's latitude, where a degree of longitude is only
 * about 72% of a degree of latitude.
 */
function nearestOnSegment(p: LatLng, a: Position, b: Position): LatLng {
  const k = Math.cos(toRad(p.lat));
  const ax = a[0] * k;
  const ay = a[1];
  const bx = b[0] * k;
  const by = b[1];
  const px = p.lng * k;
  const py = p.lat;

  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { lat: a[1], lng: a[0] };

  // Clamped so the result stays on the segment rather than its infinite line.
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  return { lat: ay + t * dy, lng: (ax + t * dx) / k };
}

/**
 * Test a point against Zone 1.
 *
 * Geometry is simplified to about 35 m, so an address within ~35 m of the
 * border may fall either way. That is well inside the noise of geocoding a
 * place of death and does not matter at this rate.
 */
export function testZone(point: LatLng, region: ZoneRegion): ZoneResult {
  const zone = ZONES[region];

  for (const feature of zone.features) {
    for (const polygon of polygonsOf(feature)) {
      const [outer, ...holes] = polygon;
      if (pointInRing(point, outer) && !holes.some((h) => pointInRing(point, h))) {
        return { inZone: true, municipality: feature.properties.name };
      }
    }
  }

  let best: { km: number; at: LatLng; name: string } | null = null;
  for (const feature of zone.features) {
    for (const polygon of polygonsOf(feature)) {
      // Outer ring only: a hole is interior to the zone, never its outer edge.
      const ring = polygon[0];
      for (let i = 0; i < ring.length - 1; i++) {
        const candidate = nearestOnSegment(point, ring[i], ring[i + 1]);
        const km = haversineKm(point, candidate);
        if (!best || km < best.km) {
          best = { km, at: candidate, name: feature.properties.name };
        }
      }
    }
  }

  if (!best) {
    // Only reachable if the GeoJSON has no rings at all, which would mean the
    // asset is broken. Say so rather than reporting a confident 0 km.
    throw new Error(`Zone 1 geometry for ${region} is empty — the asset is missing or malformed`);
  }

  return {
    inZone: false,
    straightLineKm: best.km,
    borderPoint: best.at,
    borderMunicipality: best.name,
  };
}

/** Billable kilometres and the charge, from a one-way driving distance. */
export function transportCharge(oneWayDrivingKm: number, region: ZoneRegion) {
  const billableKm = BILL_ROUND_TRIP ? oneWayDrivingKm * 2 : oneWayDrivingKm;
  return { billableKm, amount: billableKm * RATES[region].perKm };
}

/**
 * A stand-in one-way driving distance, for when routing is unavailable.
 *
 * Only ever a fallback. Mean error against the 29 reference towns is 10.2%
 * with a tail of -29% (St. Catharines), so anything derived from it has to be
 * labelled as an estimate rather than read to a family as final.
 */
export function estimateDrivingKm(straightLineKm: number) {
  return straightLineKm * CIRCUITY_FACTOR;
}

/**
 * Where a drive out of the zone actually leaves it, and how far it runs after that.
 *
 * Measuring from the *geometrically* nearest boundary point is wrong, and
 * visibly so: for Saint-Jean-sur-Richelieu the closest point on the polygon is
 * across the water from any useful road, so routing out of it detours badly and
 * bills kilometres nobody drives.
 *
 * A vehicle does not leave the zone at the nearest point — it leaves wherever
 * the road it is on crosses the boundary. So: route the real journey, walk the
 * path until it exits the zone, and bill only the part beyond that crossing.
 * That is the honest reading of "measured from the zone boundary" (§4.1), and
 * it names a crossing on an actual road, which a family can reproduce.
 */
export type BeyondZone = {
  /** One-way driving kilometres from the boundary crossing to the destination. */
  km: number;
  /** Where the route left the zone. */
  crossing: LatLng;
  /** The municipality whose edge it crossed. */
  crossingMunicipality: string;
  /** Just the beyond-the-zone part of the route, for drawing what was billed. */
  path: LatLng[];
};

/**
 * Somewhere central and unambiguously inside each zone, used only as a place to
 * route *from* so the road out can be found.
 *
 * This is not what is billed — the charge starts at the boundary crossing, not
 * here. It exists because a route needs two ends, and picking a point deep
 * inside the zone means the path reliably crosses the edge exactly once on its
 * way out.
 */
const ROUTE_ORIGIN: Record<ZoneRegion, LatLng> = {
  ontario: { lat: 43.6535, lng: -79.3839 },
  quebec: { lat: 45.5050, lng: -73.5800 },
};

/** Fine-grained path: `overview_path` is smoothed, and smoothing moves the crossing. */
function detailedPath(route: any): LatLng[] {
  const out: LatLng[] = [];
  for (const leg of route.legs ?? []) {
    for (const step of leg.steps ?? []) {
      for (const p of step.path ?? []) out.push({ lat: p.lat(), lng: p.lng() });
    }
  }
  if (out.length) return out;
  return (route.overview_path ?? []).map((p: any) => ({ lat: p.lat(), lng: p.lng() }));
}

/**
 * Driving distance beyond the zone, along the route actually taken.
 *
 * Needs the Directions API enabled on the Maps key already loaded for the
 * address autocomplete; rejects rather than guessing when it is not.
 */
export function distanceBeyondZone(
  destination: LatLng,
  region: ZoneRegion,
): Promise<BeyondZone> {
  const g = (window as any).google;
  if (!g?.maps?.DirectionsService) {
    return Promise.reject(new Error('Maps is not loaded'));
  }

  return new Promise((resolve, reject) => {
    new g.maps.DirectionsService().route(
      {
        origin: ROUTE_ORIGIN[region],
        destination,
        travelMode: g.maps.TravelMode.DRIVING,
      },
      (result: any, status: string) => {
        if (status !== 'OK' || !result?.routes?.length) {
          reject(new Error(status || 'No route'));
          return;
        }

        const path = detailedPath(result.routes[0]);
        if (path.length < 2) {
          reject(new Error('Route had no usable path'));
          return;
        }

        // The last point still inside the zone. Scanned from the destination
        // backwards, so a route that dips in and out (a boundary that follows a
        // river, say) is billed from the FINAL exit — the one that matters.
        let lastInside = -1;
        for (let i = path.length - 1; i >= 0; i--) {
          if (testZone(path[i], region).inZone) {
            lastInside = i;
            break;
          }
        }

        if (lastInside === -1) {
          reject(new Error('Route never passes through the zone'));
          return;
        }
        if (lastInside === path.length - 1) {
          reject(new Error('Route ends inside the zone'));
          return;
        }

        const inside = path[lastInside];
        const outside = path[lastInside + 1];
        const zoneHit = testZone(inside, region);

        // Bisect the crossing segment so the boundary point is accurate to a
        // few metres rather than to whatever spacing the route happened to use.
        let a = inside;
        let b = outside;
        for (let i = 0; i < 24; i++) {
          const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
          if (testZone(mid, region).inZone) a = mid;
          else b = mid;
        }
        const crossing = b;

        const beyond = [crossing, ...path.slice(lastInside + 1)];
        let km = 0;
        for (let i = 1; i < beyond.length; i++) km += haversineKm(beyond[i - 1], beyond[i]);

        resolve({
          km,
          crossing,
          crossingMunicipality: zoneHit.inZone ? zoneHit.municipality : 'the zone edge',
          path: beyond,
        });
      },
    );
  });
}
